import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, ArrowRight, GripVertical, Pencil, Lock, Unlock, Search, ChevronDown, Loader2 } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ROSTER_SIZE = 26;

const POSITIONS = [
  { value: "P", label: "Pitcher" },
  { value: "C", label: "Catcher" },
  { value: "1B", label: "First Base" },
  { value: "2B", label: "Second Base" },
  { value: "3B", label: "Third Base" },
  { value: "SS", label: "Shortstop" },
  { value: "LF", label: "Left Field" },
  { value: "CF", label: "Center Field" },
  { value: "RF", label: "Right Field" },
  { value: "DH", label: "Designated Hitter" },
];


// Sortable Batter Card (for batting order)
function SortableBatterCard({ player, index, onRemove, onEdit, onToggleSub, gameId, teamType }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: player.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const isSubbed = player.subbed_out;

  return (
    <div ref={setNodeRef} style={style} className={`game-card p-3 relative group flex items-center gap-3 ${isSubbed ? 'opacity-50' : ''}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors">
        <GripVertical size={16} />
      </div>
      <span className="text-sm font-mono text-primary font-bold w-6">{index + 1}</span>
      <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center font-mono font-bold text-primary text-xs flex-shrink-0">
        {player.position}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${isSubbed ? 'line-through text-muted-foreground' : ''}`}>{player.name}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{player.position}</p>
      </div>
      {isSubbed && (
        <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">SUBBED</span>
      )}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onToggleSub} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-amber-400 transition-colors" title={isSubbed ? "Unlock player" : "Sub out"}>
          {isSubbed ? <Unlock size={14} /> : <Lock size={14} />}
        </button>
        <button onClick={() => onEdit(player)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-primary transition-colors">
          <Pencil size={14} />
        </button>
        <button onClick={() => onRemove(player.id)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-destructive transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// Pitcher Card (not in batting order, separate section)
function PitcherCard({ player, onRemove, onEdit, onToggleSub }) {
  const isSubbed = player.subbed_out;

  return (
    <div className={`game-card p-3 relative group flex items-center gap-3 ${isSubbed ? 'opacity-50' : ''}`}>
      <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center font-mono font-bold text-red-400 text-xs flex-shrink-0">
        P
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${isSubbed ? 'line-through text-muted-foreground' : ''}`}>{player.name}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {player.pitch_chips && (
            <span className="text-[9px] text-muted-foreground bg-white/5 px-1.5 py-0.5 rounded">
              {player.pitch_chips} chips
            </span>
          )}
          {player.pitch_types?.length > 0 && (
            <span className="text-[9px] text-muted-foreground">
              {player.pitch_types.map(t => t.charAt(0).toUpperCase()).join('/')}
            </span>
          )}
        </div>
      </div>
      {isSubbed && (
        <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">SUBBED</span>
      )}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onToggleSub} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-amber-400 transition-colors" title={isSubbed ? "Unlock player" : "Sub out"}>
          {isSubbed ? <Unlock size={14} /> : <Lock size={14} />}
        </button>
        <button onClick={() => onEdit(player)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-primary transition-colors">
          <Pencil size={14} />
        </button>
        <button onClick={() => onRemove(player.id)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-destructive transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

const MLB_API = "https://statsapi.mlb.com/api/v1";

// ~130 historical MLB players for the randomize feature (Option B)
const PLAYER_POOL = [
  // Pitchers (SP)
  { name: "Greg Maddux",      pos: "P", start: 1986, end: 2008 },
  { name: "Randy Johnson",    pos: "P", start: 1988, end: 2009 },
  { name: "Roger Clemens",    pos: "P", start: 1984, end: 2007 },
  { name: "Pedro Martinez",   pos: "P", start: 1992, end: 2009 },
  { name: "Nolan Ryan",       pos: "P", start: 1966, end: 1993 },
  { name: "Tom Seaver",       pos: "P", start: 1967, end: 1986 },
  { name: "Steve Carlton",    pos: "P", start: 1965, end: 1988 },
  { name: "Bob Gibson",       pos: "P", start: 1959, end: 1975 },
  { name: "Sandy Koufax",     pos: "P", start: 1955, end: 1966 },
  { name: "Curt Schilling",   pos: "P", start: 1988, end: 2007 },
  { name: "John Smoltz",      pos: "P", start: 1988, end: 2009 },
  { name: "Roy Halladay",     pos: "P", start: 1998, end: 2013 },
  { name: "Justin Verlander", pos: "P", start: 2005, end: 2023 },
  { name: "Max Scherzer",     pos: "P", start: 2008, end: 2022 },
  { name: "Clayton Kershaw",  pos: "P", start: 2008, end: 2023 },
  { name: "Jacob deGrom",     pos: "P", start: 2014, end: 2023 },
  { name: "Johan Santana",    pos: "P", start: 2000, end: 2012 },
  { name: "Felix Hernandez",  pos: "P", start: 2005, end: 2019 },
  { name: "CC Sabathia",      pos: "P", start: 2001, end: 2019 },
  { name: "David Price",      pos: "P", start: 2008, end: 2022 },
  { name: "Bill Lee",         pos: "P", start: 1969, end: 1982 },
  // Pitchers (RP)
  { name: "Mariano Rivera",   pos: "P", start: 1995, end: 2013 },
  { name: "Trevor Hoffman",   pos: "P", start: 1993, end: 2009 },
  { name: "Lee Smith",        pos: "P", start: 1980, end: 1997 },
  { name: "Dennis Eckersley", pos: "P", start: 1975, end: 1998 },
  { name: "Goose Gossage",    pos: "P", start: 1972, end: 1994 },
  { name: "Bruce Sutter",     pos: "P", start: 1976, end: 1988 },
  { name: "Billy Wagner",     pos: "P", start: 1995, end: 2010 },
  { name: "Rollie Fingers",   pos: "P", start: 1968, end: 1985 },
  { name: "Josh Hader",       pos: "P", start: 2017, end: 2023 },
  { name: "Kenley Jansen",    pos: "P", start: 2010, end: 2023 },
  // Catchers
  { name: "Johnny Bench",     pos: "C", start: 1967, end: 1983 },
  { name: "Yogi Berra",       pos: "C", start: 1946, end: 1965 },
  { name: "Mike Piazza",      pos: "C", start: 1992, end: 2007 },
  { name: "Gary Carter",      pos: "C", start: 1974, end: 1992 },
  { name: "Carlton Fisk",     pos: "C", start: 1969, end: 1993 },
  { name: "Ivan Rodriguez",   pos: "C", start: 1991, end: 2011 },
  { name: "Joe Mauer",        pos: "C", start: 2004, end: 2018 },
  { name: "Buster Posey",     pos: "C", start: 2009, end: 2021 },
  { name: "Yadier Molina",    pos: "C", start: 2004, end: 2022 },
  { name: "Jorge Posada",     pos: "C", start: 1995, end: 2011 },
  { name: "J.T. Realmuto",    pos: "C", start: 2014, end: 2023 },
  { name: "Brian McCann",     pos: "C", start: 2005, end: 2019 },
  // First Base
  { name: "Willie McCovey",   pos: "1B", start: 1959, end: 1980 },
  { name: "Tony Perez",       pos: "1B", start: 1964, end: 1986 },
  { name: "Eddie Murray",     pos: "1B", start: 1977, end: 1997 },
  { name: "Mark McGwire",     pos: "1B", start: 1986, end: 2001 },
  { name: "Frank Thomas",     pos: "1B", start: 1990, end: 2008 },
  { name: "Jim Thome",        pos: "1B", start: 1991, end: 2012 },
  { name: "Albert Pujols",    pos: "1B", start: 2001, end: 2022 },
  { name: "Freddie Freeman",  pos: "1B", start: 2010, end: 2023 },
  { name: "Miguel Cabrera",   pos: "1B", start: 2003, end: 2023 },
  { name: "Keith Hernandez",  pos: "1B", start: 1974, end: 1990 },
  { name: "Don Mattingly",    pos: "1B", start: 1982, end: 1995 },
  { name: "Harmon Killebrew", pos: "1B", start: 1954, end: 1975 },
  // Second Base
  { name: "Joe Morgan",       pos: "2B", start: 1963, end: 1984 },
  { name: "Ryne Sandberg",    pos: "2B", start: 1981, end: 1997 },
  { name: "Roberto Alomar",   pos: "2B", start: 1988, end: 2004 },
  { name: "Craig Biggio",     pos: "2B", start: 1988, end: 2007 },
  { name: "Jeff Kent",        pos: "2B", start: 1992, end: 2008 },
  { name: "Chase Utley",      pos: "2B", start: 2003, end: 2016 },
  { name: "Dustin Pedroia",   pos: "2B", start: 2006, end: 2019 },
  { name: "Jose Altuve",      pos: "2B", start: 2011, end: 2023 },
  { name: "Robinson Cano",    pos: "2B", start: 2005, end: 2022 },
  { name: "Ian Kinsler",      pos: "2B", start: 2006, end: 2019 },
  { name: "Rod Carew",        pos: "2B", start: 1967, end: 1985 },
  { name: "Lou Whitaker",     pos: "2B", start: 1977, end: 1995 },
  // Third Base
  { name: "Mike Schmidt",     pos: "3B", start: 1972, end: 1989 },
  { name: "George Brett",     pos: "3B", start: 1973, end: 1993 },
  { name: "Wade Boggs",       pos: "3B", start: 1982, end: 1999 },
  { name: "Brooks Robinson",  pos: "3B", start: 1955, end: 1977 },
  { name: "Ron Cey",          pos: "3B", start: 1971, end: 1987 },
  { name: "Chipper Jones",    pos: "3B", start: 1993, end: 2012 },
  { name: "Scott Rolen",      pos: "3B", start: 1996, end: 2012 },
  { name: "Adrian Beltre",    pos: "3B", start: 1998, end: 2018 },
  { name: "Evan Longoria",    pos: "3B", start: 2008, end: 2023 },
  { name: "Nolan Arenado",    pos: "3B", start: 2013, end: 2023 },
  { name: "Josh Donaldson",   pos: "3B", start: 2010, end: 2023 },
  { name: "David Wright",     pos: "3B", start: 2004, end: 2018 },
  // Shortstop
  { name: "Cal Ripken Jr.",   pos: "SS", start: 1981, end: 2001 },
  { name: "Ozzie Smith",      pos: "SS", start: 1978, end: 1996 },
  { name: "Alan Trammell",    pos: "SS", start: 1977, end: 1996 },
  { name: "Derek Jeter",      pos: "SS", start: 1995, end: 2014 },
  { name: "Barry Larkin",     pos: "SS", start: 1986, end: 2004 },
  { name: "Ernie Banks",      pos: "SS", start: 1953, end: 1971 },
  { name: "Robin Yount",      pos: "SS", start: 1974, end: 1993 },
  { name: "Miguel Tejada",    pos: "SS", start: 1997, end: 2013 },
  { name: "Carlos Correa",    pos: "SS", start: 2015, end: 2023 },
  { name: "Francisco Lindor", pos: "SS", start: 2015, end: 2023 },
  { name: "Troy Tulowitzki",  pos: "SS", start: 2006, end: 2019 },
  { name: "Alex Rodriguez",   pos: "SS", start: 1994, end: 2016 },
  // Left Field
  { name: "Ted Williams",     pos: "LF", start: 1939, end: 1960 },
  { name: "Carl Yastrzemski", pos: "LF", start: 1961, end: 1983 },
  { name: "Barry Bonds",      pos: "LF", start: 1986, end: 2007 },
  { name: "Tim Raines",       pos: "LF", start: 1979, end: 2002 },
  { name: "Manny Ramirez",    pos: "LF", start: 1993, end: 2011 },
  { name: "Jim Rice",         pos: "LF", start: 1974, end: 1989 },
  { name: "Albert Belle",     pos: "LF", start: 1989, end: 2000 },
  { name: "Christian Yelich", pos: "LF", start: 2013, end: 2023 },
  { name: "Ryan Braun",       pos: "LF", start: 2007, end: 2020 },
  { name: "Rickey Henderson", pos: "LF", start: 1979, end: 2003 },
  // Center Field
  { name: "Willie Mays",      pos: "CF", start: 1951, end: 1973 },
  { name: "Mickey Mantle",    pos: "CF", start: 1951, end: 1968 },
  { name: "Ken Griffey Jr.",  pos: "CF", start: 1989, end: 2010 },
  { name: "Mike Trout",       pos: "CF", start: 2011, end: 2023 },
  { name: "Andruw Jones",     pos: "CF", start: 1996, end: 2012 },
  { name: "Jim Edmonds",      pos: "CF", start: 1993, end: 2010 },
  { name: "Carlos Beltran",   pos: "CF", start: 1998, end: 2017 },
  { name: "Torii Hunter",     pos: "CF", start: 1997, end: 2015 },
  { name: "Duke Snider",      pos: "CF", start: 1947, end: 1964 },
  { name: "Cesar Cedeno",     pos: "CF", start: 1970, end: 1986 },
  // Right Field
  { name: "Hank Aaron",       pos: "RF", start: 1954, end: 1976 },
  { name: "Babe Ruth",        pos: "RF", start: 1914, end: 1935 },
  { name: "Roberto Clemente", pos: "RF", start: 1955, end: 1972 },
  { name: "Dave Winfield",    pos: "RF", start: 1973, end: 1995 },
  { name: "Reggie Jackson",   pos: "RF", start: 1967, end: 1987 },
  { name: "Tony Gwynn",       pos: "RF", start: 1982, end: 2001 },
  { name: "Ichiro Suzuki",    pos: "RF", start: 2001, end: 2019 },
  { name: "Mookie Betts",     pos: "RF", start: 2014, end: 2023 },
  { name: "Andre Dawson",     pos: "RF", start: 1976, end: 1996 },
  { name: "Larry Walker",     pos: "RF", start: 1989, end: 2005 },
  // Designated Hitter
  { name: "Edgar Martinez",   pos: "DH", start: 1987, end: 2004 },
  { name: "David Ortiz",      pos: "DH", start: 1997, end: 2016 },
  { name: "Harold Baines",    pos: "DH", start: 1980, end: 2001 },
  { name: "Nelson Cruz",      pos: "DH", start: 2005, end: 2022 },
  { name: "Travis Hafner",    pos: "DH", start: 2002, end: 2012 },
  { name: "Paul Molitor",     pos: "DH", start: 1978, end: 1998 },
  { name: "Chili Davis",      pos: "DH", start: 1981, end: 1999 },
  { name: "Frank Thomas",     pos: "DH", start: 2000, end: 2008 },
];
const MLB_HEADSHOT = (id) => `https://securea.mlb.com/mlb/images/players/head_shot/${id}.jpg`;
const CARD_BRANDS = ["Topps","Fleer","Donruss","Bowman","Upper Deck","Score","Pacific","Leaf","Stadium Club","Pinnacle"];
const FALLBACK_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' rx='20' fill='%231e293b'/%3E%3Ccircle cx='20' cy='15' r='7' fill='%2334415a'/%3E%3Cellipse cx='20' cy='35' rx='13' ry='9' fill='%2334415a'/%3E%3C/svg%3E";
const safeImg = (e) => { e.target.onerror = null; e.target.src = FALLBACK_AVATAR; };

// Add/Edit Player Dialog
function PlayerDialog({ gameId, teamType, onPlayerSaved, editPlayer = null, open, setOpen, rosterCount, defaultPosition = "" }) {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [bats, setBats] = useState("");
  const [throws, setThrows] = useState("");
  const [pitchChips, setPitchChips] = useState("4");
  const [loading, setLoading] = useState(false);

  // Card lookup state
  const [showLookup, setShowLookup] = useState(false);
  const [cardBrand, setCardBrand] = useState("Topps");
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupSearching, setLookupSearching] = useState(false);
  const [selectedMlbPlayer, setSelectedMlbPlayer] = useState(null);
  const [lookupYear, setLookupYear] = useState("");
  const [lookupApplying, setLookupApplying] = useState(false);
  const [cardStats, setCardStats] = useState(null);
  const [cardYear, setCardYear] = useState(null);
  const [presetPlayer, setPresetPlayer] = useState(null); // full preset data when randomize hits a preset

  const isPitcher = position === "P";
  const canAddPlayer = editPlayer || rosterCount < MAX_ROSTER_SIZE;

  // Position dropdown is constrained by career data when available:
  //   edit mode  → exact historical position only
  //   preset     → natural_positions[] (all career positions this player actually played)
  //   MLB lookup → primaryPosition mapped to our codes (one position)
  //   no lookup  → defaultPosition group if set, otherwise all (manual entry)
  const availablePositions = (() => {
    if (editPlayer) return POSITIONS.filter(p => p.value === editPlayer.position);
    if (presetPlayer) {
      const allowed = new Set([presetPlayer.position, ...(presetPlayer.natural_positions || [])]);
      return POSITIONS.filter(p => allowed.has(p.value));
    }
    if (selectedMlbPlayer) {
      const abbr = selectedMlbPlayer.primaryPosition?.abbreviation || "";
      const mapped = ["TWP", "SP", "RP", "CP"].includes(abbr) ? "P" : abbr;
      return mapped ? POSITIONS.filter(p => p.value === mapped) : POSITIONS;
    }
    // No lookup yet — respect the section context (pitcher vs fielder) from defaultPosition.
    if (defaultPosition === "P") return POSITIONS.filter(p => p.value === "P");
    if (defaultPosition) return POSITIONS.filter(p => p.value !== "P");
    return POSITIONS;
  })();

  useEffect(() => {
    if (editPlayer) {
      setName(editPlayer.name);
      setPosition(editPlayer.position);
      setBats(editPlayer.bats || "");
      setThrows(editPlayer.throws || "");
      setPitchChips(String(editPlayer.pitch_chips || 4));
    } else {
      setName(""); setPosition(defaultPosition); setBats(""); setThrows(""); setPitchChips("4");
    }
    setCardStats(null); setCardYear(null); setPresetPlayer(null); setShowLookup(false);
    setLookupQuery(""); setLookupResults([]); setSelectedMlbPlayer(null); setLookupYear("");
  }, [editPlayer, open, defaultPosition]);

  // Debounced MLB player search
  useEffect(() => {
    if (!showLookup || lookupQuery.length < 2) { setLookupResults([]); return; }
    const timer = setTimeout(async () => {
      setLookupSearching(true);
      try {
        const res = await axios.get(`${MLB_API}/people/search?names=${encodeURIComponent(lookupQuery)}&sportId=1`);
        setLookupResults((res.data.people || []).slice(0, 6));
      } catch { setLookupResults([]); toast.error("Player search failed — check your connection"); }
      finally { setLookupSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [lookupQuery, showLookup]);

  const handleSelectMlbPlayer = (player) => {
    setSelectedMlbPlayer(player);
    setLookupQuery(player.fullName);
    setLookupResults([]);
  };

  const handleApplyCard = async () => {
    if (!selectedMlbPlayer || !lookupYear || lookupYear.length !== 4) {
      toast.error("Select a player and enter a 4-digit year");
      return;
    }
    const mlbIsPitcher = selectedMlbPlayer.primaryPosition?.type === "Pitcher";
    const group = mlbIsPitcher ? "pitching" : "hitting";
    setLookupApplying(true);
    try {
      const res = await axios.get(`${MLB_API}/people/${selectedMlbPlayer.id}/stats?stats=yearByYear&group=${group}&sportId=1`);
      const splits = res.data.stats?.[0]?.splits || [];
      const yearSplit = splits.find(s => s.season === String(lookupYear));
      const stat = yearSplit?.stat;

      setName(selectedMlbPlayer.fullName);
      const posAbbr = selectedMlbPlayer.primaryPosition?.abbreviation || "";
      setPosition(["TWP", "SP", "RP", "CP"].includes(posAbbr) ? "P" : (posAbbr || ""));
      setBats(selectedMlbPlayer.batSide?.code || "");
      setThrows(selectedMlbPlayer.pitchHand?.code || "");

      setCardStats(stat ? (mlbIsPitcher ? {
        era: parseFloat(stat.era) || 4.00,
        wins: stat.wins || 0,
        losses: stat.losses || 0,
        strikeouts: stat.strikeouts || 0,
        batting_average: 0, home_runs: 0, rbi: 0, stolen_bases: 0, doubles: 0, triples: 0,
      } : {
        batting_average: parseFloat(stat.avg) || 0,
        home_runs: stat.homeRuns || 0,
        rbi: stat.rbi || 0,
        stolen_bases: stat.stolenBases || 0,
        doubles: stat.doubles || 0,
        triples: stat.triples || 0,
        era: 4.00, strikeouts: 0, wins: 0, losses: 0,
      }) : null);

      setCardYear(parseInt(lookupYear));
      setShowLookup(false);
      toast.success(stat ? "Stats applied — review below" : "Player applied (no stats found for that year)");
    } catch {
      toast.error("Could not fetch stats — check year and try again");
    } finally { setLookupApplying(false); }
  };

  const handleRandomize = async () => {
    const pool = PLAYER_POOL.filter(p => !position || p.pos === position);
    if (!pool.length) { toast.error("No players in pool for that position"); return; }
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const year = pick.start + Math.floor(Math.random() * (pick.end - pick.start + 1));

    setShowLookup(false);
    setLookupQuery(pick.name);
    setSelectedMlbPlayer(null);
    setLookupYear(String(year));
    setPresetPlayer(null);
    setLookupApplying(true);
    try {
      // Check preset pool first — if we have full career data, use it directly
      const presetRes = await axios.get(`${API}/players/search?q=${encodeURIComponent(pick.name)}`);
      const presetMatch = (presetRes.data || []).find(p => p.name.toLowerCase() === pick.name.toLowerCase());
      if (presetMatch) {
        setName(presetMatch.name);
        setPosition(presetMatch.position);
        setBats(presetMatch.bats || "");
        setThrows(presetMatch.throws || "");
        setCardStats(presetMatch.stats || null);
        setCardYear(presetMatch.year || year);
        setPresetPlayer(presetMatch);
        toast.success(`Randomized: ${presetMatch.name} (${presetMatch.year || year}) — preset card`);
        return;
      }

      // Fallback: fetch from MLB Stats API
      const res = await axios.get(`${MLB_API}/people/search?names=${encodeURIComponent(pick.name)}&sportId=1`);
      const player = (res.data.people || [])[0];
      if (!player) { toast.error("Player not found in MLB database"); return; }
      setSelectedMlbPlayer(player);
      setLookupQuery(player.fullName);

      const isPitcherPos = player.primaryPosition?.type === "Pitcher";
      const group = isPitcherPos ? "pitching" : "hitting";
      const statsRes = await axios.get(`${MLB_API}/people/${player.id}/stats?stats=yearByYear&group=${group}&sportId=1`);
      const splits = statsRes.data.stats?.[0]?.splits || [];
      const yearSplit = splits.find(s => s.season === String(year)) || splits[splits.length - 1];
      const stat = yearSplit?.stat;
      const actualYear = parseInt(yearSplit?.season || year);

      setName(player.fullName);
      const posAbbr = player.primaryPosition?.abbreviation || "";
      setPosition(["TWP","SP","RP","CP"].includes(posAbbr) ? "P" : (posAbbr || pick.pos));
      setBats(player.batSide?.code || "");
      setThrows(player.pitchHand?.code || "");
      setCardStats(stat ? (isPitcherPos ? {
        era: parseFloat(stat.era) || 4.00, wins: stat.wins || 0,
        losses: stat.losses || 0, strikeouts: stat.strikeouts || 0,
        batting_average: 0, home_runs: 0, rbi: 0, stolen_bases: 0, doubles: 0, triples: 0,
      } : {
        batting_average: parseFloat(stat.avg) || 0, home_runs: stat.homeRuns || 0,
        rbi: stat.rbi || 0, stolen_bases: stat.stolenBases || 0,
        doubles: stat.doubles || 0, triples: stat.triples || 0,
        era: 4.00, strikeouts: 0, wins: 0, losses: 0,
      }) : null);
      setCardYear(actualYear);
      toast.success(`Randomized: ${player.fullName} (${actualYear})`);
    } catch {
      toast.error("Randomize failed — try again");
    } finally { setLookupApplying(false); }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !position) { toast.error("Name and position required"); return; }
    if (!canAddPlayer) { toast.error(`Roster full (max ${MAX_ROSTER_SIZE})`); return; }

    setLoading(true);
    try {
      const stats = cardStats || { batting_average: 0, home_runs: 0, rbi: 0, stolen_bases: 0, doubles: 0, triples: 0, era: 0, strikeouts: 0, wins: 0, losses: 0 };
      const playerData = {
        name, position, natural_positions: [position],
        ...(cardYear ? { year: cardYear } : {}),
        stats,
        card_condition: presetPlayer?.card_condition || "common",
        pitch_chips: presetPlayer?.pitch_chips ?? parseInt(pitchChips),
        pitch_types: [],
        bats: isPitcher ? null : (bats || null), throws: isPitcher ? (throws || null) : null,
        ...(presetPlayer?.modifiers ? { modifiers: presetPlayer.modifiers } : {}),
        ...(presetPlayer?.seasons?.length ? { seasons: presetPlayer.seasons } : {}),
        ...(presetPlayer?.active_batting_stats?.length ? { active_batting_stats: presetPlayer.active_batting_stats } : {}),
        ...(presetPlayer?.active_pitching_stats?.length ? { active_pitching_stats: presetPlayer.active_pitching_stats } : {}),
      };

      if (editPlayer) {
        await axios.put(`${API}/games/${gameId}/teams/${teamType}/players/${editPlayer.id}`, playerData);
        toast.success("Player updated");
      } else {
        await axios.post(`${API}/games/${gameId}/teams/${teamType}/players`, playerData);
        toast.success("Player added");
      }
      onPlayerSaved(); setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save player");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm bg-card border-border flex flex-col" style={{ height: "min(90vh, 600px)" }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg font-bold">{editPlayer ? "Edit Player" : "Add Player"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
          {!editPlayer && (
            <div className="text-xs text-muted-foreground text-right">Roster: {rosterCount}/{MAX_ROSTER_SIZE}</div>
          )}

          {/* Card lookup — only for new players */}
          {!editPlayer && (
            <div className="rounded-lg border border-slate-700/50 overflow-hidden">
              <button
                onClick={() => setShowLookup(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Search size={12} />
                  Fill from card
                </span>
                <ChevronDown size={12} className={`transition-transform ${showLookup ? "rotate-180" : ""}`} />
              </button>

              {showLookup && (
                <div className="p-3 border-t border-slate-700/50 space-y-3 bg-slate-900/50">
                  {/* Brand */}
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Card Brand</p>
                    <Select value={cardBrand} onValueChange={setCardBrand}>
                      <SelectTrigger className="input-dark h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CARD_BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Player search */}
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Player Name</p>
                    <div className="relative">
                      <input
                        value={lookupQuery}
                        onChange={e => { setLookupQuery(e.target.value); setSelectedMlbPlayer(null); }}
                        placeholder="Search MLB players..."
                        className="w-full px-3 py-2 pr-8 rounded-md bg-slate-800 border border-slate-700 text-white text-xs placeholder-slate-600 outline-none focus:ring-1 focus:ring-amber-500/50"
                      />
                      {lookupSearching && (
                        <div className="absolute right-2.5 top-2.5 w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" />
                      )}
                    </div>
                    {lookupResults.length > 0 && (
                      <div className="rounded-md border border-slate-700 bg-slate-800 divide-y divide-slate-700/50 overflow-hidden">
                        {lookupResults.map(p => (
                          <button
                            key={p.id}
                            onClick={() => handleSelectMlbPlayer(p)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-700/50 transition-colors"
                          >
                            <img
                              src={MLB_HEADSHOT(p.id)}
                              alt=""
                              className="w-7 h-7 rounded-full object-cover bg-slate-700 flex-shrink-0"
                              onError={safeImg}
                            />
                            <div>
                              <p className="text-xs font-semibold text-white">{p.fullName}</p>
                              <p className="text-[10px] text-slate-500">{p.primaryPosition?.abbreviation} · {p.active ? "Active" : "Retired"}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected player + year */}
                  {selectedMlbPlayer && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2.5 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                        <img
                          src={MLB_HEADSHOT(selectedMlbPlayer.id)}
                          alt=""
                          className="w-9 h-9 rounded-full object-cover bg-slate-700 flex-shrink-0"
                          onError={safeImg}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-white truncate">{selectedMlbPlayer.fullName}</p>
                          <p className="text-[10px] text-amber-400">{selectedMlbPlayer.primaryPosition?.name}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Card Year</p>
                        <input
                          value={lookupYear}
                          onChange={e => setLookupYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          placeholder="e.g. 1990"
                          className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-white text-xs placeholder-slate-600 outline-none focus:ring-1 focus:ring-amber-500/50"
                        />
                      </div>
                      <button
                        onClick={handleApplyCard}
                        disabled={lookupYear.length !== 4 || lookupApplying}
                        className="w-full py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {lookupApplying ? "Fetching stats..." : `Apply ${cardBrand} Card`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Randomize button */}
          {!editPlayer && (
            <button
              onClick={handleRandomize}
              disabled={lookupApplying}
              className="w-full py-2 rounded-lg border border-dashed border-slate-600 text-xs font-semibold text-slate-400 hover:text-white hover:border-slate-400 transition-colors disabled:opacity-40"
            >
              {lookupApplying ? "Loading…" : "⚡ Randomize Player" + (position ? ` (${position})` : "")}
            </button>
          )}

          {/* Applied stats indicator */}
          {cardStats && (
            <p className="text-[10px] text-emerald-400 flex items-center gap-1">
              ✓ {cardBrand} {cardYear} stats applied — adjust below if needed
            </p>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input placeholder="Player name" value={name} onChange={(e) => setName(e.target.value)} className="input-dark h-10" />
          </div>

          {/* Position + Bats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Position</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger data-testid="position-select" className="input-dark h-10">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {availablePositions.map(pos => <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{isPitcher ? "Throws" : "Bats"}</Label>
              {isPitcher ? (
                <Select value={throws} onValueChange={setThrows}>
                  <SelectTrigger className="input-dark h-10">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Left</SelectItem>
                    <SelectItem value="R">Right</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Select value={bats} onValueChange={setBats}>
                  <SelectTrigger className="input-dark h-10">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Left</SelectItem>
                    <SelectItem value="R">Right</SelectItem>
                    <SelectItem value="S">Switch</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Pitcher: pitch chips only */}
          {isPitcher && (
            <div className="space-y-1.5 p-3 bg-red-500/5 rounded-lg border border-red-500/10">
              <Label className="text-xs text-red-400 font-semibold uppercase tracking-wider">Pitch Chips</Label>
              <Select value={pitchChips} onValueChange={setPitchChips}>
                <SelectTrigger className="input-dark h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Chips</SelectItem>
                  <SelectItem value="4">4 Chips</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <Button onClick={handleSubmit} disabled={loading || !canAddPlayer} className="w-full h-11 btn-primary flex items-center justify-center gap-2">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? "Saving..." : editPlayer ? "Update Player" : "Add to Roster"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Bench Player Card (simpler, no drag)
function BenchPlayerCard({ player, onRemove, onEdit, onToggleSub, onMoveToLineup, canMoveToLineup }) {
  const isSubbed = player.subbed_out;
  
  return (
    <div className={`game-card p-3 relative group flex items-center gap-3 ${isSubbed ? 'opacity-50' : ''}`}>
      <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center font-mono font-bold text-blue-400 text-xs flex-shrink-0">
        {player.position}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${isSubbed ? 'line-through text-muted-foreground' : ''}`}>{player.name}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{player.position}</p>
      </div>
      {isSubbed && (
        <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">SUBBED</span>
      )}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {canMoveToLineup && !isSubbed && (
          <button onClick={() => onMoveToLineup(player)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-emerald-400 transition-colors" title="Move to lineup">
            <ArrowRight size={14} />
          </button>
        )}
        <button onClick={onToggleSub} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-amber-400 transition-colors" title={isSubbed ? "Unlock player" : "Sub out"}>
          {isSubbed ? <Unlock size={14} /> : <Lock size={14} />}
        </button>
        <button onClick={() => onEdit(player)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-primary transition-colors">
          <Pencil size={14} />
        </button>
        <button onClick={() => onRemove(player.id)} className="p-1.5 rounded hover:bg-white/5 text-muted-foreground hover:text-destructive transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// Team Roster with 3 sections: Active Lineup (1-9), Bench, Pitchers
function TeamRoster({ game, teamType, gameId, onUpdate, isGameActive, onOrderChange }) {
  const team = teamType === "away" ? game.away_team : game.home_team;
  const allPlayers = team.players || [];
  
  // Separate pitchers from position players
  const positionPlayers = allPlayers.filter(p => p.position !== "P");
  const pitchers = allPlayers.filter(p => p.position === "P");
  
  // Active lineup is first 9, bench is the rest
  const [lineupOrder, setLineupOrder] = useState(positionPlayers.slice(0, 9));
  const [benchPlayers, setBenchPlayers] = useState(positionPlayers.slice(9));
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPlayer, setEditPlayer] = useState(null);
  const [dialogDefaultPosition, setDialogDefaultPosition] = useState("");
  const [orderLocked, setOrderLocked] = useState(isGameActive);
  const [hasChanges, setHasChanges] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const nonPitchers = (team.players || []).filter(p => p.position !== "P");
    setLineupOrder(nonPitchers.slice(0, 9));
    setBenchPlayers(nonPitchers.slice(9));
  }, [team.players]);

  useEffect(() => {
    setOrderLocked(isGameActive);
  }, [isGameActive]);

  const handleDragEnd = (event) => {
    if (orderLocked) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = lineupOrder.findIndex(p => p.id === active.id);
    const newIndex = lineupOrder.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(lineupOrder, oldIndex, newIndex);
    setLineupOrder(newOrder);
    setHasChanges(true);
    onOrderChange(teamType, newOrder.map(p => p.id));
  };

  const handleToggleLock = async () => {
    if (!orderLocked && hasChanges && isGameActive) {
      try {
        await axios.put(`${API}/games/${gameId}/teams/${teamType}/batting-order`, lineupOrder.map(p => p.id));
        toast.success("Batting order saved");
        setHasChanges(false);
      } catch (error) {
        toast.error("Failed to save order");
        return;
      }
    }
    setOrderLocked(!orderLocked);
  };

  const handleRemove = async (playerId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/teams/${teamType}/players/${playerId}`);
      toast.success("Removed"); onUpdate();
    } catch (error) { toast.error("Failed"); }
  };

  const handleToggleSub = async (playerId) => {
    try {
      await axios.put(`${API}/games/${gameId}/teams/${teamType}/players/${playerId}/toggle-sub`);
      onUpdate();
    } catch (error) { toast.error("Failed to toggle sub status"); }
  };

  const handleMoveToLineup = (player) => {
    if (lineupOrder.length >= 9) {
      toast.error("Lineup is full (9 players max)");
      return;
    }
    const newLineup = [...lineupOrder, player];
    const newBench = benchPlayers.filter(p => p.id !== player.id);
    setLineupOrder(newLineup);
    setBenchPlayers(newBench);
    setHasChanges(true);
    onOrderChange(teamType, newLineup.map(p => p.id));
  };

  const handleMoveToBench = (player) => {
    const newLineup = lineupOrder.filter(p => p.id !== player.id);
    const newBench = [...benchPlayers, player];
    setLineupOrder(newLineup);
    setBenchPlayers(newBench);
    setHasChanges(true);
    onOrderChange(teamType, newLineup.map(p => p.id));
  };

  const handleEdit = (player) => { setEditPlayer(player); setDialogDefaultPosition(""); setDialogOpen(true); };
  const handleAdd = (defaultPos = "") => { setEditPlayer(null); setDialogDefaultPosition(defaultPos); setDialogOpen(true); };

  return (
    <div className="space-y-4">
      {/* Roster info */}
      <div className="game-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <h2 className="section-header">{team.name}</h2>
          </div>
          <span className="text-xs text-muted-foreground">
            {allPlayers.length}/{MAX_ROSTER_SIZE} players
          </span>
        </div>
      </div>

      {/* Active Batting Lineup (1-9) */}
      <div className="game-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-primary uppercase tracking-wider">Active Lineup</p>
            <span className="text-[10px] text-muted-foreground bg-white/5 px-1.5 py-0.5 rounded">{lineupOrder.length}/9</span>
            {orderLocked && (
              <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20 flex items-center gap-1">
                <Lock size={10} /> Locked
              </span>
            )}
          </div>
          {isGameActive ? (
            <button 
              onClick={handleToggleLock} 
              className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1"
            >
              {orderLocked ? <><Unlock size={10} /> Unlock</> : <><Lock size={10} /> Save & Lock</>}
            </button>
          ) : (
            <span className="text-[10px] text-muted-foreground">Drag to reorder</span>
          )}
        </div>
        
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={lineupOrder.map(p => p.id)} strategy={verticalListSortingStrategy}>
            <div className={`space-y-2 ${orderLocked ? 'opacity-60 pointer-events-none' : ''}`}>
              {lineupOrder.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Add batters to your lineup (max 9)</p>
              ) : (
                lineupOrder.map((player, idx) => (
                  <SortableBatterCard 
                    key={player.id} 
                    player={player} 
                    index={idx}
                    onRemove={handleRemove} 
                    onEdit={handleEdit}
                    onToggleSub={() => handleToggleSub(player.id)}
                    onMoveToBench={() => handleMoveToBench(player)}
                    gameId={gameId}
                    teamType={teamType}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Bench Players */}
      <div className="game-card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Bench</p>
          <span className="text-[10px] text-muted-foreground">{benchPlayers.length} player{benchPlayers.length !== 1 ? 's' : ''}</span>
        </div>
        
        <div className="space-y-2">
          {benchPlayers.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No bench players</p>
          ) : (
            benchPlayers.map(player => (
              <BenchPlayerCard 
                key={player.id} 
                player={player} 
                onRemove={handleRemove} 
                onEdit={handleEdit}
                onToggleSub={() => handleToggleSub(player.id)}
                onMoveToLineup={handleMoveToLineup}
                canMoveToLineup={lineupOrder.length < 9}
              />
            ))
          )}
        </div>
      </div>

      {/* Pitching Staff */}
      <div className="game-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">Pitching Staff</p>
            <span className="text-[10px] text-muted-foreground">{pitchers.length} pitcher{pitchers.length !== 1 ? 's' : ''}</span>
            {pitchers.length < 2 && (
              <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                need {2 - pitchers.length} more
              </span>
            )}
          </div>
          <button
            onClick={() => handleAdd("P")}
            disabled={allPlayers.length >= MAX_ROSTER_SIZE}
            className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={11} /> Add Pitcher
          </button>
        </div>

        <div className="space-y-2">
          {pitchers.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No pitchers yet — add at least 2</p>
          ) : (
            pitchers.map(player => (
              <PitcherCard
                key={player.id}
                player={player}
                onRemove={handleRemove}
                onEdit={handleEdit}
                onToggleSub={() => handleToggleSub(player.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Roster requirements checklist */}
      {!isGameActive && (() => {
        const REQUIRED_FIELD_POSITIONS = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH"];
        const missing = REQUIRED_FIELD_POSITIONS.filter(pos => !allPlayers.some(p => p.position === pos));
        const needMorePitchers = pitchers.length < 2;
        if (missing.length === 0 && !needMorePitchers) return null;
        return (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 space-y-1">
            <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Required to start</p>
            {needMorePitchers && (
              <p className="text-xs text-amber-300/80">✗ Need {2 - pitchers.length} more pitcher{2 - pitchers.length > 1 ? "s" : ""} (min 2)</p>
            )}
            {missing.map(pos => (
              <p key={pos} className="text-xs text-amber-300/80">✗ Missing {POSITIONS.find(p => p.value === pos)?.label || pos}</p>
            ))}
          </div>
        );
      })()}

      {/* Add Position Player Button */}
      <Button
        data-testid={`add-player-${teamType}-btn`}
        variant="outline"
        onClick={() => handleAdd()}
        disabled={allPlayers.length >= MAX_ROSTER_SIZE}
        className="w-full border-dashed border-white/10 hover:border-primary/50 hover:bg-primary/5 h-11 transition-colors"
      >
        <Plus className="mr-2" size={16} /> Add Position Player
      </Button>

      <PlayerDialog
        gameId={gameId}
        teamType={teamType}
        onPlayerSaved={onUpdate}
        editPlayer={editPlayer}
        open={dialogOpen}
        setOpen={setDialogOpen}
        rosterCount={allPlayers.length}
        defaultPosition={dialogDefaultPosition}
      />
    </div>
  );
}

export default function TeamSetup() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnline = searchParams.get("online") === "1";
  const onlineRole = searchParams.get("role") || "home";

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOrders, setPendingOrders] = useState({ away: null, home: null });

  // WS for online mode — notify opponent of roster changes
  const wsRef = useRef(null);
  useEffect(() => {
    if (!isOnline) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001").replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/ws/${gameId}/${onlineRole}`);
    wsRef.current = ws;
    return () => ws.close();
  }, [gameId, isOnline, onlineRole]);

  const fetchGame = async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}`);
      setGame(response.data);
      if (isOnline && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "roster_update" }));
      }
    }
    catch (error) { toast.error("Couldn't load game"); navigate("/"); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGame(); }, [gameId]);

  const handleOrderChange = (teamType, order) => {
    setPendingOrders(prev => ({ ...prev, [teamType]: order }));
  };

  const handleStartGame = async () => {
    const REQUIRED_FIELD_POSITIONS = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH"];
    for (const teamData of [game.away_team, game.home_team]) {
      const pitchers = teamData.players.filter(p => p.position === "P");
      if (pitchers.length < 2) {
        toast.error(`${teamData.name} needs at least 2 pitchers (has ${pitchers.length})`);
        return;
      }
      const missing = REQUIRED_FIELD_POSITIONS.filter(pos => !teamData.players.some(p => p.position === pos));
      if (missing.length > 0) {
        const posLabel = (v) => POSITIONS.find(p => p.value === v)?.label || v;
        toast.error(`${teamData.name} is missing: ${missing.map(posLabel).join(", ")}`);
        return;
      }
    }
    try {
      // Save pending batting orders before starting
      if (pendingOrders.away) {
        await axios.put(`${API}/games/${gameId}/teams/away/batting-order`, pendingOrders.away);
      }
      if (pendingOrders.home) {
        await axios.put(`${API}/games/${gameId}/teams/home/batting-order`, pendingOrders.home);
      }
      await axios.post(`${API}/games/${gameId}/start`);
      navigate(`/game/${gameId}`);
    }
    catch (error) { toast.error("Failed to start"); }
  };

  const handleBackToGame = () => {
    if (game.status === "active") {
      navigate(`/game/${gameId}`);
    } else {
      navigate("/");
    }
  };

  const isGameActive = game?.status === "active";

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button data-testid="back-btn" variant="ghost" onClick={handleBackToGame} className="text-sm h-9 hover:bg-white/5">
            <ArrowLeft className="mr-2" size={16} /> {isGameActive ? "Back to Game" : "Back"}
          </Button>
          <h1 className="text-lg font-bold tracking-tight">Roster Setup</h1>
          {isGameActive ? (
            <Button onClick={() => navigate(`/game/${gameId}`)} className="btn-primary h-9 px-5">
              Return to Game <ArrowRight className="ml-2" size={16} />
            </Button>
          ) : (
            <Button data-testid="start-game-btn" onClick={handleStartGame} className="btn-primary h-9 px-5">
              Start Game <ArrowRight className="ml-2" size={16} />
            </Button>
          )}
        </div>

        <Tabs defaultValue="away" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-5 bg-black/30 p-1 rounded-lg h-11">
            <TabsTrigger data-testid="away-team-tab" value="away" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md font-medium transition-all">
              {game?.away_team.name} <span className="ml-1.5 opacity-60">({game?.away_team.players.length || 0})</span>
            </TabsTrigger>
            <TabsTrigger data-testid="home-team-tab" value="home" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md font-medium transition-all">
              {game?.home_team.name} <span className="ml-1.5 opacity-60">({game?.home_team.players.length || 0})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="away"><TeamRoster game={game} teamType="away" gameId={gameId} onUpdate={fetchGame} isGameActive={isGameActive} onOrderChange={handleOrderChange} /></TabsContent>
          <TabsContent value="home"><TeamRoster game={game} teamType="home" gameId={gameId} onUpdate={fetchGame} isGameActive={isGameActive} onOrderChange={handleOrderChange} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
